import { fabric } from 'fabric';
import { GRIP_GAP, GRIP_SIZE, handleShown, pickHandle, type HandleHit, type PieceKind } from './handleRules';

/*
 * ─── ที่จับชิ้นงานเล็ก: wiring the rules (src/editor/handleRules.ts) into Fabric 5.3 ─────────────────────────────────────
 * Done once for every canvas, like ./arrows:
 *   1. Control.getVisibility — Fabric asks it both to draw a handle and to hit-test it, so hiding here hides both;
 *   2. Object._findTargetCorner — the same polygon test as Fabric's, then pickHandle (inside = move, nearest wins);
 *   3. a ✥ `grip` control below a tiny piece, dragging it with Fabric's own drag (object:moving → snapping as usual).
 */

type Obj = fabric.Object & {
  oCoords: Record<string, { x: number; y: number; corner: unknown; touchCorner: unknown }>;
  _getImageLines(c: unknown): unknown;
  _findCrossPoints(p: { x: number; y: number }, lines: unknown): number;
  __corner: string | 0;
  touchCornerSize?: number;
};

/** the piece's size on the screen (px) */
export function screenSize(o: fabric.Object) {
  const z = o.canvas?.getZoom() ?? 1;
  return { w: Math.abs(o.getScaledWidth() * z), h: Math.abs(o.getScaledHeight() * z) };
}
/** text box (ours is 'thaiTextbox') / line / anything else — see handleShown */
const kindOf = (o: fabric.Object): PieceKind => o instanceof fabric.Textbox ? 'text' : o.type === 'line' ? 'line' : 'other';

/* 1 — which handles show */
const baseVisibility = fabric.Control.prototype.getVisibility;
fabric.Control.prototype.getVisibility = function (this: fabric.Control, o: fabric.Object, key: string) {
  if (!baseVisibility.call(this, o, key)) return false;
  const { w, h } = screenSize(o);
  return handleShown(key, w, h, kindOf(o));
};

/* 2 — which handle a point grabs */
(fabric.Object.prototype as unknown as { _findTargetCorner: unknown })._findTargetCorner = function (this: Obj, pointer: { x: number; y: number }, forTouch?: boolean) {
  // as Fabric: pieces inside a group, or not the chosen one, have no handles to grab
  if (!this.hasControls || this.group || !this.canvas || (this.canvas as unknown as { _activeObject?: unknown })._activeObject !== this) return false;
  this.__corner = 0;
  const hits: HandleHit[] = [];
  const keys = Object.keys(this.oCoords);
  for (let j = keys.length - 1; j >= 0; j--) {     // Fabric's order: the handle drawn on top first
    const key = keys[j];
    if (!this.isControlVisible(key)) continue;
    const c = this.oCoords[key];
    const n = this._findCrossPoints(pointer, this._getImageLines(forTouch ? c.touchCorner : c.corner));
    if (n !== 0 && n % 2 === 1) hits.push({ key, d: Math.hypot(pointer.x - c.x, pointer.y - c.y) });
  }
  if (!hits.length) return false;
  const { w, h } = screenSize(this);
  const half = (forTouch ? (this.touchCornerSize ?? 24) : (this.cornerSize ?? 13)) / 2;
  const key = pickHandle(hits, this.containsPoint(new fabric.Point(pointer.x, pointer.y)), Math.min(w, h), half);
  if (!key) return false;
  this.__corner = key;
  return key;
};

/* 3 — the ✥ grip below a tiny piece */
function renderGrip(this: fabric.Control, ctx: CanvasRenderingContext2D, left: number, top: number, _s: unknown, o: fabric.Object) {
  const r = GRIP_SIZE / 2, a = (o.cornerColor as string) || '#7c5cff';
  ctx.save();
  ctx.translate(left, top);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff'; ctx.fill();
  ctx.lineWidth = 2; ctx.strokeStyle = a; ctx.stroke();
  // four arrows
  ctx.strokeStyle = a; ctx.fillStyle = a; ctx.lineWidth = 1.6; ctx.lineCap = 'round';
  const L = r - 5, t = 3.2;
  ctx.beginPath(); ctx.moveTo(-L, 0); ctx.lineTo(L, 0); ctx.moveTo(0, -L); ctx.lineTo(0, L); ctx.stroke();
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    ctx.beginPath();
    ctx.moveTo(dx * L, dy * L);
    ctx.lineTo(dx * (L - t) - dy * t, dy * (L - t) - dx * t);
    ctx.lineTo(dx * (L - t) + dy * t, dy * (L - t) + dx * t);
    ctx.closePath(); ctx.fill();
  }
  ctx.restore();
}
const grip = new fabric.Control({
  x: 0, y: 0.5, offsetY: GRIP_GAP,
  sizeX: GRIP_SIZE, sizeY: GRIP_SIZE, touchSizeX: 48, touchSizeY: 48,
  actionHandler: (fabric as unknown as { controlsUtils: { dragHandler: fabric.Control['actionHandler'] } }).controlsUtils.dragHandler,
  actionName: 'drag',
  cursorStyle: 'move',
  render: renderGrip,
});
// Fabric's Textbox has its own controls object (the others share Object's): add the grip to both
(fabric.Object.prototype.controls as Record<string, fabric.Control>).grip = grip;
(fabric.Textbox.prototype.controls as Record<string, fabric.Control>).grip = grip;
